/**
 * ShapeRenderer - Renders all shape types
 * Supports: rounded rectangle, pill rectangle, circle, rounded triangles (up/down), rounded rhombus, curved arrow line, parallel lines
 * Implements gradients, shadows, strokes, and text inside shapes with Figma-style rounded corners (12px)
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement, ShapeType } from '../editing/types';
import { COLORS } from '../utils/constants';
import { applyGradientFill, isGradientFill } from '../utils/gradientUtils';
import { applyShadows, applyStrokeStyle, applyBlurEffect, applyBlendMode } from '../utils/shapeStyleUtils';
import { getPrimaryFill, getPrimaryStroke, getPrimaryStrokeWidth } from '../utils/multipleFillsUtils';
import { applyCornerRadius, getDefaultStarConfig, getDefaultPolygonConfig, getDefaultArrowConfig } from '../utils/shapeConfigUtils';

export class ShapeRenderer extends BaseRenderer {
  private shapeNode: Konva.Shape | Konva.Group | null = null;
  private textNode: Konva.Text | null = null;
  private shapeType: ShapeType;

  constructor(element: EditableElement) {
    super(element);
    this.shapeType = element.data?.shapeType || 'rectangle-rounded';
  }

  /**
   * Render the shape element
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    
    // Create the shape based on type
    this.shapeNode = this.createShape();
    this._node.add(this.shapeNode);
    
    // Add text if present
    if (data?.text && data.text.trim()) {
      this.textNode = this.createTextNode();
      this._node.add(this.textNode);
    }
    
    // Apply effects after adding to group
    this.applyEffects();
    
    return this._node;
  }

  /**
   * Create the shape based on type
   */
  private createShape(): Konva.Shape | Konva.Group {
    const { width, height, data } = this.element;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2;
    
    // Get fill and stroke
    const primaryFill = getPrimaryFill(data?.fills, data?.fillGradient || data?.fill);
    const primaryStroke = getPrimaryStroke(data?.strokes, data?.stroke);
    const primaryStrokeWidth = getPrimaryStrokeWidth(data?.strokes, data?.strokeWidth);
    
    const fillValue = primaryFill || COLORS.YELLOW;
    const isGradient = isGradientFill(fillValue);
    
    // Base props
    const baseProps: Konva.ShapeConfig = {
      stroke: primaryStroke || '#6B7280', // Default dark gray
      strokeWidth: primaryStrokeWidth || 4, // Default 4px
      opacity: data?.opacity ?? 1,
    };
    
    // Apply fill (solid color only - gradient applied later via effect)
    if (!isGradient) {
      baseProps.fill = fillValue as string;
    }
    
    // Apply shadows
    if (data?.shadows && Array.isArray(data.shadows) && data.shadows.length > 0) {
      baseProps.shadowColor = data.shadows[0].color;
      baseProps.shadowBlur = data.shadows[0].blur;
      baseProps.shadowOffset = { x: data.shadows[0].offsetX, y: data.shadows[0].offsetY };
      baseProps.shadowOpacity = data.shadows[0].opacity;
    }
    
    // Apply stroke style
    if (data?.strokeStyle) {
      const strokeStyle = data.strokeStyle;
      if (strokeStyle.pattern === 'dashed') {
        baseProps.dash = [10, 5];
      } else if (strokeStyle.pattern === 'dotted') {
        baseProps.dash = [2, 5];
      } else if (strokeStyle.pattern === 'custom' && strokeStyle.dashArray) {
        baseProps.dash = strokeStyle.dashArray;
      }
    }
    
    // Get corner radius (default 12px for rounded shapes)
    const cornerRadius = typeof data?.cornerRadius === 'number' 
      ? data.cornerRadius 
      : data?.cornerRadius?.uniform || 12;
    
    switch (this.shapeType) {
      case 'rectangle-rounded':
        return new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
        
      case 'rectangle-pill':
        // Pill shape: fully rounded ends (corner radius = min(width, height) / 2)
        const pillRadius = Math.min(width, height) / 2;
        return new Konva.Rect({
          width,
          height,
          cornerRadius: pillRadius,
          ...baseProps,
        });
        
      case 'circle':
        return new Konva.Circle({
          x: centerX,
          y: centerY,
          radius,
          ...baseProps,
        });
        
      case 'triangle-up':
        return this.createRoundedTriangle(width, height, centerX, centerY, radius, cornerRadius, baseProps, false);
        
      case 'triangle-down':
        return this.createRoundedTriangle(width, height, centerX, centerY, radius, cornerRadius, baseProps, true);
        
      case 'diamond':
        return this.createDiamond(width, height, centerX, centerY, radius, cornerRadius, baseProps);
        
      case 'line-curved-arrow':
        return this.createCurvedArrowLine(width, height, centerY, baseProps);
        
      case 'line-parallel':
        return this.createParallelLines(width, height, baseProps);
        
      default:
        // Fallback to rounded rectangle
        return new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
    }
  }

  /**
   * Create rounded triangle (up or down)
   */
  private createRoundedTriangle(
    width: number,
    height: number,
    centerX: number,
    centerY: number,
    radius: number,
    cornerRadius: number,
    baseProps: Konva.ShapeConfig,
    isDown: boolean
  ): Konva.Shape {
    return new Konva.Shape({
      ...baseProps,
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        const ctx = context;
        // Use width and height to create a triangle that respects the element dimensions
        // Base spans the full width, height spans the full height
        const halfBase = width / 2;
        const triangleHeight = height;
        // Use corner radius directly, but limit to reasonable size relative to triangle
        const r = Math.min(cornerRadius, Math.min(halfBase, triangleHeight) * 0.3);
        
        // Define vertices in order (clockwise or counter-clockwise)
        let points: Array<{ x: number; y: number }>;
        
        if (isDown) {
          // Downward triangle (pointing down) - base at top, point at bottom
          points = [
            { x: centerX, y: centerY + triangleHeight / 2 }, // Bottom vertex (point)
            { x: centerX - halfBase, y: centerY - triangleHeight / 2 }, // Top-left
            { x: centerX + halfBase, y: centerY - triangleHeight / 2 }, // Top-right
          ];
        } else {
          // Upward triangle (pointing up) - base at bottom, point at top
          points = [
            { x: centerX, y: centerY - triangleHeight / 2 }, // Top vertex (point)
            { x: centerX + halfBase, y: centerY + triangleHeight / 2 }, // Bottom-right
            { x: centerX - halfBase, y: centerY + triangleHeight / 2 }, // Bottom-left
          ];
        }
        
        ctx.beginPath();
        
        // Start from first point, adjusted for corner radius
        // Move to a point before the first corner (coming from last point)
        const lastPoint = points[points.length - 1];
        const firstPoint = points[0];
        const secondPoint = points[1];
        
        // Calculate direction from last to first point
        const dx = firstPoint.x - lastPoint.x;
        const dy = firstPoint.y - lastPoint.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        const unitX = dx / len;
        const unitY = dy / len;
        
        // Start point: first vertex offset by radius along the incoming edge
        const startX = firstPoint.x - unitX * r;
        const startY = firstPoint.y - unitY * r;
        ctx.moveTo(startX, startY);
        
        // Draw rounded corners using arcTo
        // arcTo(x1, y1, x2, y2, radius) creates an arc from current point
        // through (x1, y1) towards (x2, y2) with given radius
        for (let i = 0; i < points.length; i++) {
          const current = points[i];
          const next = points[(i + 1) % points.length];
          const afterNext = points[(i + 2) % points.length];
          
          // Use arcTo to create rounded corner at current vertex
          // The arc goes from current position, through current vertex, towards next vertex
          ctx.arcTo(current.x, current.y, next.x, next.y, r);
        }
        
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      },
      hitFunc: (context: Konva.Context, shape: Konva.Shape) => {
        // Use same path for hit detection to match visual bounds exactly
        const ctx = context;
        // Use width and height to create a triangle that respects the element dimensions
        const halfBase = width / 2;
        const triangleHeight = height;
        const r = Math.min(cornerRadius, Math.min(halfBase, triangleHeight) * 0.3);
        
        let points: Array<{ x: number; y: number }>;
        
        if (isDown) {
          points = [
            { x: centerX, y: centerY + triangleHeight / 2 },
            { x: centerX - halfBase, y: centerY - triangleHeight / 2 },
            { x: centerX + halfBase, y: centerY - triangleHeight / 2 },
          ];
        } else {
          points = [
            { x: centerX, y: centerY - triangleHeight / 2 },
            { x: centerX + halfBase, y: centerY + triangleHeight / 2 },
            { x: centerX - halfBase, y: centerY + triangleHeight / 2 },
          ];
        }
        
        ctx.beginPath();
        
        const lastPoint = points[points.length - 1];
        const firstPoint = points[0];
        
        const dx = firstPoint.x - lastPoint.x;
        const dy = firstPoint.y - lastPoint.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        const unitX = dx / len;
        const unitY = dy / len;
        
        const startX = firstPoint.x - unitX * r;
        const startY = firstPoint.y - unitY * r;
        ctx.moveTo(startX, startY);
        
        for (let i = 0; i < points.length; i++) {
          const current = points[i];
          const next = points[(i + 1) % points.length];
          ctx.arcTo(current.x, current.y, next.x, next.y, r);
        }
        
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      },
    });
  }

  /**
   * Create diamond shape (rectangle rotated 45° with rounded corners)
   */
  private createDiamond(
    width: number,
    height: number,
    centerX: number,
    centerY: number,
    radius: number,
    cornerRadius: number,
    baseProps: Konva.ShapeConfig
  ): Konva.Shape {
    return new Konva.Shape({
      ...baseProps,
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        const ctx = context;
        // Use width and height to create a diamond that respects the element dimensions
        // Diamond vertices are at the midpoints of each side of the bounding rectangle
        const halfWidth = width / 2;
        const halfHeight = height / 2;
        const r = Math.min(cornerRadius, Math.min(halfWidth, halfHeight) * 0.4); // Limit corner radius
        
        // Calculate diamond vertices (rectangle rotated 45°)
        // Top, right, bottom, left vertices at midpoints of bounding rectangle sides
        const topX = centerX;
        const topY = centerY - halfHeight;
        const rightX = centerX + halfWidth;
        const rightY = centerY;
        const bottomX = centerX;
        const bottomY = centerY + halfHeight;
        const leftX = centerX - halfWidth;
        const leftY = centerY;
        
        ctx.beginPath();
        
        // Start from top vertex, going clockwise
        // Calculate angles for rounded corners
        const angleTopToRight = Math.atan2(rightY - topY, rightX - topX);
        const angleRightToBottom = Math.atan2(bottomY - rightY, bottomX - rightX);
        const angleBottomToLeft = Math.atan2(leftY - bottomY, leftX - bottomX);
        const angleLeftToTop = Math.atan2(topY - leftY, topX - leftX);
        
        // Top corner (rounded)
        const topStartX = topX + r * Math.cos(angleLeftToTop + Math.PI);
        const topStartY = topY + r * Math.sin(angleLeftToTop + Math.PI);
        ctx.moveTo(topStartX, topStartY);
        ctx.arcTo(topX, topY, rightX, rightY, r);
        
        // Right corner (rounded)
        const rightStartX = rightX + r * Math.cos(angleTopToRight + Math.PI);
        const rightStartY = rightY + r * Math.sin(angleTopToRight + Math.PI);
        ctx.lineTo(rightStartX, rightStartY);
        ctx.arcTo(rightX, rightY, bottomX, bottomY, r);
        
        // Bottom corner (rounded)
        const bottomStartX = bottomX + r * Math.cos(angleRightToBottom + Math.PI);
        const bottomStartY = bottomY + r * Math.sin(angleRightToBottom + Math.PI);
        ctx.lineTo(bottomStartX, bottomStartY);
        ctx.arcTo(bottomX, bottomY, leftX, leftY, r);
        
        // Left corner (rounded)
        const leftStartX = leftX + r * Math.cos(angleBottomToLeft + Math.PI);
        const leftStartY = leftY + r * Math.sin(angleBottomToLeft + Math.PI);
        ctx.lineTo(leftStartX, leftStartY);
        ctx.arcTo(leftX, leftY, topX, topY, r);
        
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      },
      hitFunc: (context: Konva.Context, shape: Konva.Shape) => {
        // Use same path for hit detection to match visual bounds exactly
        const ctx = context;
        // Use width and height to create a diamond that respects the element dimensions
        const halfWidth = width / 2;
        const halfHeight = height / 2;
        const r = Math.min(cornerRadius, Math.min(halfWidth, halfHeight) * 0.4);
        
        // Calculate diamond vertices (same as sceneFunc)
        const topX = centerX;
        const topY = centerY - halfHeight;
        const rightX = centerX + halfWidth;
        const rightY = centerY;
        const bottomX = centerX;
        const bottomY = centerY + halfHeight;
        const leftX = centerX - halfWidth;
        const leftY = centerY;
        
        const angleTopToRight = Math.atan2(rightY - topY, rightX - topX);
        const angleRightToBottom = Math.atan2(bottomY - rightY, bottomX - rightX);
        const angleBottomToLeft = Math.atan2(leftY - bottomY, leftX - bottomX);
        const angleLeftToTop = Math.atan2(topY - leftY, topX - leftX);
        
        ctx.beginPath();
        const topStartX = topX + r * Math.cos(angleLeftToTop + Math.PI);
        const topStartY = topY + r * Math.sin(angleLeftToTop + Math.PI);
        ctx.moveTo(topStartX, topStartY);
        ctx.arcTo(topX, topY, rightX, rightY, r);
        
        const rightStartX = rightX + r * Math.cos(angleTopToRight + Math.PI);
        const rightStartY = rightY + r * Math.sin(angleTopToRight + Math.PI);
        ctx.lineTo(rightStartX, rightStartY);
        ctx.arcTo(rightX, rightY, bottomX, bottomY, r);
        
        const bottomStartX = bottomX + r * Math.cos(angleRightToBottom + Math.PI);
        const bottomStartY = bottomY + r * Math.sin(angleRightToBottom + Math.PI);
        ctx.lineTo(bottomStartX, bottomStartY);
        ctx.arcTo(bottomX, bottomY, leftX, leftY, r);
        
        const leftStartX = leftX + r * Math.cos(angleBottomToLeft + Math.PI);
        const leftStartY = leftY + r * Math.sin(angleBottomToLeft + Math.PI);
        ctx.lineTo(leftStartX, leftStartY);
        ctx.arcTo(leftX, leftY, topX, topY, r);
        
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      },
    });
  }

  /**
   * Create curved arrow line (S-shaped with arrow)
   */
  private createCurvedArrowLine(
    width: number,
    height: number,
    centerY: number,
    baseProps: Konva.ShapeConfig
  ): Konva.Group {
    const group = new Konva.Group();
    
    // Create S-shaped curved path using bezier curves
    const controlPoint1X = width * 0.3;
    const controlPoint1Y = centerY - height * 0.3;
    const controlPoint2X = width * 0.7;
    const controlPoint2Y = centerY + height * 0.3;
    
    const curvedLine = new Konva.Shape({
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
      lineCap: 'round',
      lineJoin: 'round',
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        const ctx = context;
        ctx.beginPath();
        ctx.moveTo(0, centerY);
        ctx.bezierCurveTo(
          controlPoint1X, controlPoint1Y,
          controlPoint2X, controlPoint2Y,
          width, centerY
        );
        ctx.strokeShape(shape);
      },
    });
    group.add(curvedLine);
    
    // Add arrow head at the end
    const arrowSize = Math.min(15, width * 0.1);
    const arrowHead = new Konva.Shape({
      fill: baseProps.stroke,
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        const ctx = context;
        // Calculate angle at end of curve
        const endAngle = Math.atan2(
          controlPoint2Y - centerY,
          width - controlPoint2X
        );
        
        ctx.beginPath();
        ctx.moveTo(width, centerY);
        ctx.lineTo(
          width - arrowSize * Math.cos(endAngle - Math.PI / 6),
          centerY - arrowSize * Math.sin(endAngle - Math.PI / 6)
        );
        ctx.lineTo(
          width - arrowSize * Math.cos(endAngle + Math.PI / 6),
          centerY - arrowSize * Math.sin(endAngle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      },
    });
    group.add(arrowHead);
    
    return group;
  }

  /**
   * Create parallel lines
   */
  private createParallelLines(
    width: number,
    height: number,
    baseProps: Konva.ShapeConfig
  ): Konva.Group {
    const group = new Konva.Group();
    const spacing = Math.min(10, height * 0.2);
    const centerY = height / 2;
    
    // First line
    const line1 = new Konva.Line({
      points: [0, centerY - spacing / 2, width, centerY - spacing / 2],
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
      lineCap: 'round',
    });
    group.add(line1);
    
    // Second line
    const line2 = new Konva.Line({
      points: [0, centerY + spacing / 2, width, centerY + spacing / 2],
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
      lineCap: 'round',
    });
    group.add(line2);
    
    return group;
  }

  /**
   * Create double-ended arrow (kept for backward compatibility if needed)
   */
  private createDoubleEndedArrow(
    width: number,
    height: number,
    centerY: number,
    arrowConfig: { pointerLength: number; pointerWidth: number },
    baseProps: Konva.ShapeConfig
  ): Konva.Group {
    const group = new Konva.Group();
    
    // Main line
    const line = new Konva.Line({
      points: [arrowConfig.pointerLength, centerY, width - arrowConfig.pointerLength, centerY],
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
    });
    group.add(line);
    
    // Start arrowhead (pointing left)
    const startArrow = new Konva.Shape({
      fill: baseProps.fill || baseProps.stroke,
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        context.beginPath();
        context.moveTo(arrowConfig.pointerLength, centerY);
        context.lineTo(0, centerY - arrowConfig.pointerWidth / 2);
        context.lineTo(0, centerY + arrowConfig.pointerWidth / 2);
        context.closePath();
        context.fillStrokeShape(shape);
      },
    });
    group.add(startArrow);
    
    // End arrowhead (pointing right)
    const endArrow = new Konva.Shape({
      fill: baseProps.fill || baseProps.stroke,
      stroke: baseProps.stroke,
      strokeWidth: baseProps.strokeWidth,
      opacity: baseProps.opacity,
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        context.beginPath();
        context.moveTo(width - arrowConfig.pointerLength, centerY);
        context.lineTo(width, centerY - arrowConfig.pointerWidth / 2);
        context.lineTo(width, centerY + arrowConfig.pointerWidth / 2);
        context.closePath();
        context.fillStrokeShape(shape);
      },
    });
    group.add(endArrow);
    
    return group;
  }

  /**
   * Create text node for shape
   */
  private createTextNode(): Konva.Text {
    const { width, height, data } = this.element;
    
    return new Konva.Text({
      text: data?.text?.trim() || '',
      width,
      height,
      fontSize: data?.fontSize || data?.textStyle?.fontSize || 16,
      fontFamily: data?.fontFamily || data?.textStyle?.fontFamily || 'Arial',
      fontStyle: data?.fontStyle || data?.textStyle?.fontStyle || 'normal',
      fill: data?.textFill || data?.textStyle?.fill || '#000000',
      align: data?.align || data?.textStyle?.align || 'center',
      verticalAlign: 'middle',
      padding: 8,
      wrap: 'word',
      lineHeight: data?.textStyle?.lineHeight,
      letterSpacing: data?.textStyle?.letterSpacing,
    });
  }

  /**
   * Apply effects (gradients, shadows, etc.)
   */
  private applyEffects(): void {
    if (!this.shapeNode) return;
    
    const { width, height, data } = this.element;
    const primaryFill = getPrimaryFill(data?.fills, data?.fillGradient || data?.fill);
    const fillValue = primaryFill || COLORS.YELLOW;
    const isGradient = isGradientFill(fillValue);
    
    // Get the target node (for groups, we need to apply to children)
    const targetNode = this.shapeNode instanceof Konva.Group 
      ? this.shapeNode.getChildren()[0] 
      : this.shapeNode;
    
    if (targetNode) {
      if (isGradient) {
        applyGradientFill(targetNode, fillValue, width, height);
      }
      if (data?.shadows && Array.isArray(data.shadows) && data.shadows.length > 0) {
        applyShadows(targetNode, data.shadows);
      }
      if (data?.strokeStyle) {
        applyStrokeStyle(targetNode, data.strokeStyle);
      }
      if (data?.blur) {
        applyBlurEffect(targetNode, data.blur);
      }
      if (data?.blendMode) {
        applyBlendMode(targetNode, data.blendMode);
      }
    }
  }

  /**
   * Update the shape with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    // If shape type changed or dimensions changed significantly, rebuild
    if (updates.data?.shapeType || updates.width !== undefined || updates.height !== undefined) {
      this.rebuild();
      return;
    }
    
    // Update fill, stroke, etc.
    if (updates.data && this.shapeNode) {
      const targetNode = this.shapeNode instanceof Konva.Group 
        ? this.shapeNode.getChildren()[0] as Konva.Shape
        : this.shapeNode;
      
      if (targetNode && !(targetNode instanceof Konva.Group)) {
        if (updates.data.fill !== undefined) {
          targetNode.fill(updates.data.fill);
        }
        if (updates.data.stroke !== undefined) {
          targetNode.stroke(updates.data.stroke);
        }
        if (updates.data.strokeWidth !== undefined) {
          targetNode.strokeWidth(updates.data.strokeWidth);
        }
        if (updates.data.opacity !== undefined) {
          targetNode.opacity(updates.data.opacity);
        }
      }
    }
    
    // Update text
    if (updates.data && this.textNode) {
      if (updates.data.text !== undefined) {
        this.textNode.text(updates.data.text?.trim() || '');
      }
      if (updates.data.fontSize !== undefined) {
        this.textNode.fontSize(updates.data.fontSize);
      }
      if (updates.data.fontFamily !== undefined) {
        this.textNode.fontFamily(updates.data.fontFamily);
      }
      if (updates.data.fontStyle !== undefined) {
        this.textNode.fontStyle(updates.data.fontStyle);
      }
      if (updates.data.textFill !== undefined) {
        this.textNode.fill(updates.data.textFill);
      }
      if (updates.data.align !== undefined) {
        this.textNode.align(updates.data.align);
      }
    }
    
    // Handle text visibility
    if (updates.data?.text !== undefined) {
      if (updates.data.text && updates.data.text.trim() && !this.textNode) {
        this.textNode = this.createTextNode();
        this._node.add(this.textNode);
      } else if (this.textNode && (!updates.data.text || !updates.data.text.trim())) {
        this.textNode.destroy();
        this.textNode = null;
      }
    }
    
    this.applyEffects();
  }

  /**
   * Rebuild the shape (for dimension or type changes)
   */
  private rebuild(): void {
    // Remove old nodes
    if (this.shapeNode) {
      this.shapeNode.destroy();
    }
    if (this.textNode) {
      this.textNode.destroy();
    }
    
    // Update shape type if changed
    if (this.element.data?.shapeType) {
      this.shapeType = this.element.data.shapeType;
    }
    
    // Recreate nodes
    this.shapeNode = this.createShape();
    this._node.add(this.shapeNode);
    
    if (this.element.data?.text && this.element.data.text.trim()) {
      this.textNode = this.createTextNode();
      this._node.add(this.textNode);
    } else {
      this.textNode = null;
    }
    
    this.applyEffects();
  }

  /**
   * Set dimensions and update internal nodes
   */
  setDimensions(width: number, height: number): void {
    super.setDimensions(width, height);
    this.rebuild();
  }

  /**
   * Hide text (for text editing overlay)
   */
  hideText(): void {
    if (this.textNode) {
      this.textNode.visible(false);
    }
  }

  /**
   * Show text
   */
  showText(): void {
    if (this.textNode) {
      this.textNode.visible(true);
    }
  }

  /**
   * Get shape type
   */
  getShapeType(): ShapeType {
    return this.shapeType;
  }

  /**
   * Get fill color
   */
  getFillColor(): string {
    return this.element.data?.fill || COLORS.YELLOW;
  }

  /**
   * Get stroke color
   */
  getStrokeColor(): string {
    return this.element.data?.stroke || '#000000';
  }

  /**
   * Get text content
   */
  getText(): string {
    return this.element.data?.text || '';
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.shapeNode = null;
    this.textNode = null;
    super.destroy();
  }
}
